import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import LoadingModal from './components/modals/LoadingModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import ResponsabilidadeModal from './components/modalServices/ResponsabilidadeModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';

function Responsabilidades() {
  const [responsabilidades, setResponsabilidades] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [filterType, setFilterType] = useState('TODOS');
  const [selectedResponsabilidade, setSelectedResponsabilidade] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingResponsabilidade, setEditingResponsabilidade] = useState(null);
  const [cnpjAtual, setCnpjAtual] = useState('');

  const token = localStorage.getItem('token');
  const location = useLocation();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  const normalizeString = (str) => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  // Filtro por tipo de permissão
  let responsabilidadesFiltrados = responsabilidades;
  if (filterType !== 'TODOS') {
    responsabilidadesFiltrados = responsabilidadesFiltrados.filter((r) => {
      if (filterType === 'DFD') return r.dfd === 'S';
      if (filterType === 'APROVA_DFD') return r.aprovaDfd === 'S';
      if (filterType === 'PCA') return r.pca === 'S';
      return true;
    });
  }

  // Filtro de busca
  responsabilidadesFiltrados = responsabilidadesFiltrados.filter((r) => {
    const normalizedBusca = normalizeString(filtro);
    return (
      normalizeString(r.nome).includes(normalizedBusca) ||
      normalizeString(r.cpf).includes(normalizedBusca) ||
      normalizeString(r.cargo).includes(normalizedBusca)
    );
  });

  const totalItems = responsabilidadesFiltrados.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedResponsabilidades = responsabilidadesFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  useEffect(() => {
    const state = location.state;
    if (state && state.cnpj) {
      setCnpjAtual(state.cnpj);
      fetchResponsabilidades(state.cnpj);
    } else {
      setLoading(false);
    }
  }, [location]);

  const fetchResponsabilidades = async (cnpj) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL.RESPONSABILIDADE}/cnpj?cnpj=${cnpj}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setResponsabilidades(response.data);
    } catch (error) {
      console.error('Erro ao buscar responsabilidades:', error);
      showDefaultModal('error', 'Erro ao carregar responsabilidades.');
    } finally {
      setLoading(false);
    }
  };

  // Gerenciar seleção/desseleção
  const handleRowClick = (responsabilidade) => {
    if (
      selectedResponsabilidade &&
      selectedResponsabilidade.cnpj === responsabilidade.cnpj &&
      selectedResponsabilidade.id === responsabilidade.id
    ) {
      setSelectedResponsabilidade(null);
    } else {
      setSelectedResponsabilidade(responsabilidade);
    }
  };

  const handleOpenModal = (responsabilidade = null) => {
    setEditingResponsabilidade(responsabilidade);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingResponsabilidade(null);
  };

  const handleModalSuccess = () => {
    setAtualizar(!atualizar);
    fetchResponsabilidades(cnpjAtual);
    setSelectedResponsabilidade(null);
    showDefaultModal('success', 'Responsabilidade salva com sucesso!');
  };

  const handleDelete = (responsabilidade) => {
    showConfirmModal('delete', () => handleConfirmDelete(responsabilidade));
  };

  const handleConfirmDelete = (responsabilidade) => {
    axios
      .delete(
        `${API_BASE_URL.RESPONSABILIDADE}/delete?cnpj=${responsabilidade.cnpj}&id=${responsabilidade.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => {
        setAtualizar(!atualizar);
        fetchResponsabilidades(cnpjAtual);
        setSelectedResponsabilidade(null);
        showDefaultModal('success', 'Responsabilidade excluída com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao deletar:', error);
        showDefaultModal('error', 'Erro ao excluir responsabilidade!');
      });
  };

  const toolbarActions = [
    {
      key: 'novo',
      label: 'Novo',
      icon: 'bi-plus-circle',
      variant: 'success',
      onClick: () => handleOpenModal(),
      disabled: !cnpjAtual,
    },
    {
      key: 'editar',
      label: 'Editar',
      icon: 'bi-pencil-square',
      variant: 'primary',
      onClick: (resp) => resp && handleOpenModal(resp),
      requiresSelection: true,
    },
    {
      key: 'excluir',
      label: 'Excluir',
      icon: 'bi-trash3',
      variant: 'danger',
      onClick: (resp) => resp && handleDelete(resp),
      requiresSelection: true,
    },
  ];

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Responsabilidades" icon="bi-person-badge" backButton={false} />
        <PageContent>
          <Toolbar
            selectedId={selectedResponsabilidade}
            actions={toolbarActions}
            searchValue={filtro}
            onSearch={(v) => setFiltro((v||'').toUpperCase())}
            searchWidth="220px"
          >
            <div className="d-flex align-items-center">
              <label className="form-label me-2 mb-0 small text-muted">Filtro:</label>
              <select
                className="form-select form-select-sm"
                style={{ width: '180px' }}
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="TODOS">Todos</option>
                <option value="DFD">Responsável DFD</option>
                <option value="APROVA_DFD">Aprova DFD</option>
                <option value="PCA">Responsável PCA</option>
              </select>
            </div>
          </Toolbar>

          <LoadingModal loading={isLoading} />

          <TableContainer tableClass="table-custom-radius">
            <TableHeader columns={[
              { label: '#', width: '50px' },
              { label: 'Nome' },
              { label: 'CPF', width: '110px' },
              { label: 'Matrícula', width: '110px' },
              { label: 'Cargo' },
              { label: 'DFD', width: '70px' },
              { label: 'Aprova DFD', width: '100px' },
              { label: 'PCA', width: '70px' },
            ]} />
            <tbody>
              {!isLoading && selectedResponsabilidades.length > 0 ? (
                selectedResponsabilidades.map((resp, index) => {
                  const isSelected =
                    selectedResponsabilidade &&
                    selectedResponsabilidade.cnpj === resp.cnpj &&
                    selectedResponsabilidade.id === resp.id;
                  return (
                    <tr
                      key={`${resp.cnpj}-${resp.id}`}
                      onClick={() => handleRowClick(resp)}
                      style={{ cursor: 'pointer' }}
                      className={isSelected ? 'table-active' : ''}
                      title={isSelected ? 'Clique novamente para desmarcar' : 'Clique para selecionar'}
                    >
                      <td className={isSelected ? 'fw-bold text-primary' : ''}>{setPagination.startIndex + index + 1}</td>
                      <td className={isSelected ? 'fw-bold text-primary' : ''}>{resp.nome}</td>
                      <td className={isSelected ? 'fw-bold text-primary' : ''}>{resp.cpf}</td>
                      <td className={isSelected ? 'fw-bold text-primary' : ''}>{resp.matricula}</td>
                      <td className={isSelected ? 'fw-bold text-primary' : ''}>{resp.cargo}</td>
                      <td className={isSelected ? 'fw-bold text-primary' : ''}>
                        <span className={`badge ${resp.dfd === 'S' ? 'bg-success' : 'bg-secondary'}`}>
                          {resp.dfd === 'S' ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td className={isSelected ? 'fw-bold text-primary' : ''}>
                        <span className={`badge ${resp.aprovaDfd === 'S' ? 'bg-success' : 'bg-secondary'}`}>
                          {resp.aprovaDfd === 'S' ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td className={isSelected ? 'fw-bold text-primary' : ''}>
                        <span className={`badge ${resp.pca === 'S' ? 'bg-success' : 'bg-secondary'}`}>
                          {resp.pca === 'S' ? 'Sim' : 'Não'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center text-muted">
                    Nenhuma responsabilidade encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </TableContainer>

          <PageFooter>
            <Pagination10 {...setPagination} />
          </PageFooter>

          <ResponsabilidadeModal
            isOpen={isModalOpen}
            responsabilidade={editingResponsabilidade}
            cnpjAtual={cnpjAtual}
            onClose={handleCloseModal}
            onSuccess={handleModalSuccess}
          />

          <ConfirmModal {...setConfirmModal} />
          <DefaultModal {...setDefaultModal} />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default Responsabilidades;

